import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../../config/environment';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      
      // Store token and user info
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));
      
      // Redirect to admin dashboard
      navigate('/admin');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <title>Admin Login | KGOB Events</title>
      
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-8 shadow-soft border border-[#E1DFD7]">
            {/* Logo/Title */}
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl font-semibold text-[#0B2240] mb-2">
                KGOB Events Admin
              </h1>
              <p className="text-[#4C5A70] text-sm">
                Sign in to manage events and RSVPs
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#0B2240] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full h-11 px-4 rounded-[12px] border border-[#E1DFD7] bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA3A3]"
                  placeholder="admin@kgob.com"
                  data-testid="admin-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0B2240] mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full h-11 px-4 rounded-[12px] border border-[#E1DFD7] bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA3A3]"
                  placeholder="Enter password"
                  data-testid="admin-password-input"
                />
              </div>

              {error && (
                <div className="bg-[#C7362E]/10 border border-[#C7362E] rounded-lg p-4">
                  <p className="text-[#C7362E] text-sm font-medium">⚠️ {error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#0B2240] text-white rounded-[12px] font-semibold hover:bg-[#0A1D35] disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-[#1FA3A3] focus-visible:outline-none"
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signing in...
                  </span>
                ) : 'Sign In'}
              </button>
            </form>
          </div>

          {/* Back to public site */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/events')}
              className="text-[#1FA3A3] hover:underline text-sm"
            >
              ← Back to Events
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
